import { Button, Columns, LegacyCard, RadioButton, Stack, Text, Toast } from "@shopify/polaris";
import { Field } from "react-final-form";
import SelectedLocationsField from "../../../../../common/SelectedLocationsField/SelectedLocationsField.js";
import React, { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import { useAppQuery, useAppMutation } from "../../../../../../hooks/index.js";
import { ACTION_TYPE_ATTRIBUTES } from "../../../../../../../constants/actionTypes.js";

interface LocationPermissionsResponse {
  hasLocationPermissions: boolean;
  permissionUrl?: string;
}

interface RefreshLocationsResponse {
  inventoryLocations: Array<{
    id: string;
    name: string;
    address: string;
    shipsInventory: boolean;
  }>;
  refreshedAt: string;
  message: string;
  locationChanges: {
    detected: number;
    changes: Array<{
      type: 'new' | 'name_change';
      locationName: string;
      locationId: string;
    }>;
  };
  productResync: {
    queued: number;
    failed: number;
    message: string;
  };
}

const LocationSettingsCard = ({ values, actionType }) => {
  const [locationMode, setLocationMode] = useState(
    values.locationSettings?.monitorAllLocations !== false ? "all" : "specific"
  );
  const [refreshToast, setRefreshToast] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data: permissionData, isLoading: permissionLoading } = useAppQuery<LocationPermissionsResponse, Error>({
    url: "/api/shop/location-permissions",
    reactQueryOptions: {
      staleTime: 5 * 60 * 1000 // 5 minutes
    },
    fetchInit: {}
  });

  const { mutate: refreshLocationsMutate, isLoading: isRefreshingLocations } = useAppMutation<RefreshLocationsResponse, Error>({
    url: "/api/products/refresh-inventory-locations",
    reactMutationOptions: {
      onSuccess: (data) => {
        // Invalidate the inventory-locations cache so SelectedLocationsField refetches
        queryClient.invalidateQueries(["/api/products/inventory-locations"]);

        // Create user-friendly success message (hide resync details)
        let toastMessage = `Successfully refreshed ${data.inventoryLocations.length} locations`;

        if (data.locationChanges.detected > 0) {
          const newLocations = data.locationChanges.changes.filter(c => c.type === 'new').length;
          const nameChanges = data.locationChanges.changes.filter(c => c.type === 'name_change').length;

          let changeDetails = [];
          if (newLocations > 0) changeDetails.push(`${newLocations} new location${newLocations > 1 ? 's' : ''}`);
          if (nameChanges > 0) changeDetails.push(`${nameChanges} name change${nameChanges > 1 ? 's' : ''}`);

          toastMessage += `. Detected ${changeDetails.join(' and ')}.`;
          // Note: Removed product resync details from user notification
        }

        setRefreshToast(toastMessage);
      },
      onError: (error) => {
        setRefreshToast(`Failed to refresh locations: ${error.message}`);
      }
    }
  });

  const handleLocationModeChange = useCallback(
    (checked: boolean, id: string) => {
      if (checked) {
        setLocationMode(id);
      }
    },
    []
  );

  const handleAddPermissions = useCallback(() => {
    if (permissionData?.permissionUrl) {
      window.open(permissionData.permissionUrl, '_top');
    }
  }, [permissionData?.permissionUrl]);

  const handleRefreshLocations = useCallback(() => {
    refreshLocationsMutate({});
  }, [refreshLocationsMutate]);

  const handleToastDismiss = useCallback(() => {
    setRefreshToast(null);
  }, []);

  const hasLocationPermissions = permissionData?.hasLocationPermissions ?? true; // Default to true while loading
  const showPermissionError = !permissionLoading && !hasLocationPermissions;

  return (
    <>
      <LegacyCard>
        <LegacyCard.Section>
          <Stack vertical spacing="tight">
            <Stack distribution="equalSpacing" alignment="center">
              <Stack>
                <RadioButton
                  label="All locations"
                  checked={locationMode === "all"}
                  id="all"
                  name="locationMode"
                  onChange={handleLocationModeChange}
                />
                <RadioButton
                  label="Specific locations"
                  checked={locationMode === "specific"}
                  id="specific"
                  name="locationMode"
                  onChange={handleLocationModeChange}
                  disabled={showPermissionError}
                />
              </Stack>
              <Button
                size="slim"
                onClick={handleRefreshLocations}
                loading={isRefreshingLocations}
                disabled={isRefreshingLocations}
              >
                Refresh locations
              </Button>
            </Stack>

            {showPermissionError && (
              <Stack vertical spacing="tight">
                <Text variant="bodyMd" as="p" color="critical">
                  Location permissions are required to monitor specific locations.
                </Text>
                <div>
                  <Button onClick={handleAddPermissions} size="slim">
                    Add location permission
                  </Button>
                </div>
              </Stack>
            )}

            {locationMode === "specific" && hasLocationPermissions && (
              <div style={{ paddingLeft: "1.75rem" }}>
                <Field
                  name="locationSettings.selectedLocationNames"
                  component={SelectedLocationsField}
                  labelHidden={false}
                />
              </div>
            )}

            {/* Hidden fields to store the form data */}
            <Field
              name="locationSettings.monitorAllLocations"
              render={({ input }) => {
                // Update the value based on radio selection
                if (locationMode === "all" && !input.value) {
                  input.onChange(true);
                } else if (locationMode === "specific" && input.value !== false) {
                  input.onChange(false);
                }
                return null;
              }}
            />
          </Stack>
        </LegacyCard.Section>
      </LegacyCard>

      {refreshToast && (
        <Toast
          content={refreshToast}
          onDismiss={handleToastDismiss}
          duration={6000}
        />
      )}
    </>
  );
};

export default LocationSettingsCard;